<template>
  <div>
    <v-dialog v-model="dialogOpen" persistent width="900">
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="secondary" class="white--text">
          <v-toolbar-title class="font-weight-bold">
            {{ $t('carbonCredit.bulkUpload.dialogTitle') }}
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            class="white--text"
            :disabled="isBusy"
            @click="closeDialog"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <div style="position: relative;">
          <div
            v-if="isBusy"
            class="d-flex align-center justify-center white flex-column px-4 py-5"
          >
            <v-progress-circular :size="75" color="primary" indeterminate />
            <p class="mt-10 text-center mb-0">
              {{ busyMessage }}
            </p>
          </div>

          <div v-show="!isBusy" class="px-4 py-3">
            <v-card elevation="0">
              <v-tabs
                v-model="tab"
                class="tabs-pill"
                background-color="grey lighten-3"
              >
                <v-tab class="text-capitalize">{{ $t('carbonCredit.bulkUpload.tabUpload') }}</v-tab>
                <v-tab class="text-capitalize" :disabled="!previewData || importFailed">
                  {{ $t('carbonCredit.bulkUpload.tabPreview') }}
                </v-tab>
              </v-tabs>
            </v-card>

            <v-tabs-items v-model="tab">
              <!-- Upload -->
              <v-tab-item>
                <v-card elevation="0" flat>
                  <v-card-text style="padding: 0 !important" class="mt-3">
                    {{ $t('carbonCredit.bulkUpload.templateNote') }}
                  </v-card-text>

                  <v-btn
                    color="primary"
                    class="my-3"
                    :href="templateDownloadUrl"
                    download
                    target="_blank"
                    rel="noopener"
                  >
                    {{ $t('carbonCredit.bulkUpload.downloadTemplate') }}
                    <v-icon small class="ml-2">mdi-tray-arrow-down</v-icon>
                  </v-btn>

                  <div class="dropzone">
                    <label
                      v-if="!file"
                      class="d-flex flex-column align-center justify-center px-3 py-3"
                      :class="{ active: dragging }"
                      @dragenter.prevent="dragging = true"
                      @dragleave.prevent="dragging = false"
                      @drop.prevent="onDrop"
                      @dragover.prevent
                    >
                      <img width="50" src="/img/xlsx-upload.svg" alt="" />
                      <input
                        class="d-none"
                        type="file"
                        accept=".xlsx,.xls"
                        @change="onFileChange"
                      />
                      <span class="font-weight-medium pt-2 text-decoration-underline blue--text darken-4">
                        {{ $t('carbonCredit.bulkUpload.selectFile') }}
                      </span>
                      <span>{{ $t('carbonCredit.bulkUpload.dropHere') }}</span>
                    </label>
                    <div
                      v-else
                      class="d-flex flex-column justify-center align-center"
                      style="position: relative; width: 100%; height: 100%;"
                    >
                      <img width="50" src="/img/xlsx-upload.svg" alt="" />
                      <div class="mt-1">
                        <span class="text-body-1">{{ fileName }}</span>
                      </div>
                      <v-alert
                        dense
                        type="error"
                        class="mt-4"
                        style="min-width: 70%;"
                        outlined
                        v-show="fileError"
                      >
                        {{ fileError }}
                      </v-alert>
                      <v-btn
                        icon
                        text
                        class="ma-2"
                        style="position: absolute; top: 0; right: 0;"
                        @click="clearFile"
                      >
                        <v-icon dark>mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <p class="pt-3 text-body-2 mb-0">
                    <span class="red--text">{{ $t('carbonCredit.bulkUpload.importantNote') }}</span>:
                    {{ $t('carbonCredit.bulkUpload.importantNoteDetail') }}
                  </p>

                  <v-alert
                    v-if="importFailed && importErrorList.length"
                    type="error"
                    outlined
                    dense
                    class="mt-4 mb-0"
                  >
                    <div class="font-weight-bold mb-2">
                      {{ $t('carbonCredit.bulkUpload.importFailed') }}
                    </div>
                    <p class="mb-2 text-body-2">
                      {{ $t('carbonCredit.bulkUpload.importFailedIntro') }}
                    </p>
                    <ul class="bulk-upload-message-list mb-3 pl-4">
                      <li v-for="err in importErrorList" :key="err.id" class="mb-1">
                        <strong>{{ err.location }}</strong> — {{ err.message }}
                      </li>
                    </ul>
                    <p class="mb-0 text-body-2">
                      {{ $t('carbonCredit.bulkUpload.importRetryHint') }}
                    </p>
                  </v-alert>

                  <div class="bulk-upload-org-field mt-3">
                    <v-autocomplete
                      v-model="selectedOrganizationId"
                      :items="organizationList"
                      item-text="name"
                      item-value="id"
                      outlined
                      dense
                      :loading="loadingOrganizations"
                      :disabled="!isSuperAdmin || loadingOrganizations"
                      :placeholder="$t('carbonCredit.bulkUpload.selectOrganization')"
                      :label="$t('carbonCredit.bulkUpload.organizationLabel')"
                      :filter="filterOrganizations"
                      clearable
                      hide-details="auto"
                      @change="onOrganizationChange"
                    />
                  </div>

                  <v-divider class="my-5 px-0" />

                  <v-card-actions class="px-0">
                    <v-spacer />
                    <v-btn
                      class="mr-2"
                      outlined
                      color="primary"
                      width="112"
                      @click="closeDialog"
                    >
                      {{ $t('cancel') }}
                    </v-btn>
                    <v-btn
                      width="112"
                      color="primary"
                      class="btn-11 btn-22"
                      :disabled="!canPreview"
                      @click="runPreview"
                    >
                      {{ $t('carbonCredit.bulkUpload.preview') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-tab-item>

              <!-- Preview -->
              <v-tab-item>
                <v-card elevation="0" flat class="px-0 py-4">
                  <v-alert
                    v-if="isDryRunPreview"
                    type="info"
                    outlined
                    dense
                    class="mb-4"
                  >
                    {{ $t('carbonCredit.bulkUpload.dryRunNote') }}
                  </v-alert>

                  <div v-if="selectedOrganizationName" class="mb-3">
                    <span class="grey--text text-body-2">
                      {{ $t('carbonCredit.bulkUpload.organizationLabel') }}:
                    </span>
                    <span class="text-body-2 font-weight-medium">{{ selectedOrganizationName }}</span>
                  </div>

                  <template v-if="showPreviewStats">
                  <div>
                    <span class="grey--text text-body-2">
                      {{ previewSuccess ? $t('carbonCredit.bulkUpload.previewReady') : $t('carbonCredit.bulkUpload.previewHasIssues') }}
                    </span>
                  </div>
                  <div class="d-flex align-center mt-1 mb-2">
                    <v-progress-linear
                      rounded
                      height="8"
                      :value="previewProgressPercent"
                      :color="previewSuccess ? 'primary' : 'warning'"
                    />
                    <span
                      class="font-weight-bold ml-2"
                      :class="previewSuccess ? 'primary--text' : 'warning--text'"
                    >
                      {{ previewProgressPercent }}%
                    </span>
                  </div>
                  <div class="mb-3">
                    <span class="grey--text text-body-2" style="text-transform: lowercase;">
                      {{ previewStats.success }} {{ $t('of') }} {{ previewStats.total }} {{ $t('recordsImported') }}
                    </span>
                  </div>

                  <div class="d-flex justify-space-between grey lighten-4 py-3 px-4 mb-4">
                    <div class="d-flex align-center flex-column">
                      <span class="text-h4">{{ previewStats.total }}</span>
                      <span class="text-body-2">{{ $t('totalRecords') }}</span>
                    </div>
                    <div class="d-flex align-center flex-column">
                      <span class="text-h4 red--text">{{ previewStats.failed }}</span>
                      <span class="text-body-2">{{ $t('failed') }}</span>
                    </div>
                    <div class="d-flex align-center flex-column">
                      <span class="text-h4 primary--text">{{ previewStats.success }}</span>
                      <span class="text-body-2">{{ $t('success') }}</span>
                    </div>
                  </div>
                  </template>

                  <v-alert
                    v-if="previewErrorList.length"
                    type="error"
                    outlined
                    dense
                    class="mt-4 mb-0"
                  >
                    <div class="font-weight-bold mb-2">
                      {{ $t('carbonCredit.bulkUpload.errorsTitle', { count: previewErrorList.length }) }}
                    </div>
                    <ul class="bulk-upload-message-list mb-0 pl-4">
                      <li v-for="err in previewErrorList" :key="err.id" class="mb-1">
                        <strong>{{ err.location }}</strong> — {{ err.message }}
                      </li>
                    </ul>
                  </v-alert>

                  <v-alert
                    v-if="previewWarningList.length"
                    type="warning"
                    outlined
                    dense
                    class="mt-4 mb-0"
                  >
                    <div class="font-weight-bold mb-2">
                      {{ $t('carbonCredit.bulkUpload.warningsTitle', { count: previewWarningList.length }) }}
                    </div>
                    <ul class="bulk-upload-message-list mb-0 pl-4">
                      <li v-for="w in previewWarningList" :key="w.id" class="mb-1">
                        <strong>{{ w.location }}</strong> — {{ w.message }}
                      </li>
                    </ul>
                  </v-alert>

                  <v-card class="py-4 mt-4" elevation="1" style="max-height: 480px; overflow: auto;">
                    <h3 class="subtitle-2 font-weight-bold px-4 mb-2">
                      {{ $t('carbonCredit.bulkUpload.sectionProjects') }}
                    </h3>
                    <v-data-table
                      :headers="projectHeadersWithMessage"
                      :items="previewProjects"
                      item-key="rowKey"
                      dense
                      disable-pagination
                      hide-default-footer
                      class="bulk-upload-table"
                    >
                      <template v-slot:item.action="{ item }">
                        <v-chip
                          x-small
                          :color="actionChipColor(item.action)"
                          :outlined="item.action !== 'error'"
                          :dark="item.action === 'error'"
                          class="justify-center"
                          style="min-width: 72px;"
                        >
                          {{ actionLabel(item.action) }}
                        </v-chip>
                      </template>
                      <template v-slot:item.importStatus="{ item }">
                        <v-chip
                          style="width: 90px;"
                          class="justify-center"
                          :color="rowImportStatusColor(item)"
                          outlined
                        >
                          {{ rowImportStatusLabel(item) }}
                        </v-chip>
                      </template>
                      <template v-slot:item.message="{ item }">
                        <span v-if="item.message" class="error--text caption">{{ item.message }}</span>
                        <span v-else class="grey--text">—</span>
                      </template>
                    </v-data-table>

                    <h3 class="subtitle-2 font-weight-bold px-4 mb-2 mt-4">
                      {{ $t('carbonCredit.bulkUpload.sectionFarmLinks') }}
                    </h3>
                    <v-data-table
                      :headers="farmHeaders"
                      :items="previewFarmLinks"
                      item-key="rowKey"
                      dense
                      disable-pagination
                      hide-default-footer
                      class="bulk-upload-table"
                    >
                      <template v-slot:item.action="{ item }">
                        <v-chip
                          x-small
                          :color="actionChipColor(item.action)"
                          :outlined="item.action !== 'error'"
                          :dark="item.action === 'error'"
                          class="justify-center"
                          style="min-width: 72px;"
                        >
                          {{ farmActionLabel(item.action) }}
                        </v-chip>
                      </template>
                      <template v-slot:item.importStatus="{ item }">
                        <v-chip
                          style="min-width: 100px;"
                          class="justify-center"
                          :color="rowImportStatusColor(item, { farmApplication: true })"
                          outlined
                        >
                          {{ rowImportStatusLabel(item, { farmApplication: true }) }}
                        </v-chip>
                      </template>
                      <template v-slot:item.message="{ item }">
                        <span v-if="item.message" class="error--text caption">{{ item.message }}</span>
                        <span v-else class="grey--text">—</span>
                      </template>
                    </v-data-table>

                    <h3 class="subtitle-2 font-weight-bold px-4 mb-2 mt-4">
                      {{ $t('carbonCredit.bulkUpload.sectionPlotRequests') }}
                    </h3>
                    <v-expansion-panels accordion flat class="px-2 pb-2">
                      <v-expansion-panel
                        v-for="req in previewPlotRequests"
                        :key="req.rowKey"
                      >
                        <v-expansion-panel-header class="px-2">
                          <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                            <strong>{{ req.requestId || '—' }}</strong>
                            <span class="caption grey--text">
                              {{ $t('carbonCredit.bulkUpload.colFarmId') }}: {{ req.farmId != null ? req.farmId : '—' }}
                            </span>
                            <v-chip
                              x-small
                              :color="req.importStatus === 'ok' ? 'primary' : 'error'"
                              outlined
                            >
                              {{ req.importStatus === 'ok' ? $t('success') : $t('failed') }}
                            </v-chip>
                          </div>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content>
                          <p v-if="req.message" class="error--text caption mb-2">{{ req.message }}</p>
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th>{{ $t('carbonCredit.bulkUpload.colRow') }}</th>
                                <th>{{ $t('carbonCredit.bulkUpload.colPlotNo') }}</th>
                                <th>{{ $t('carbonCredit.bulkUpload.colLocation') }}</th>
                                <th>{{ $t('carbonCredit.bulkUpload.colRadius') }}</th>
                                <th>{{ $t('carbonCredit.bulkUpload.colAssignee') }}</th>
                                <th>{{ $t('oma.status') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="plot in req.plots" :key="`${req.rowKey}-${plot.row}`">
                                <td>{{ plot.row }}</td>
                                <td>{{ plot.plotNo }}</td>
                                <td>
                                  <template v-if="plot.latitude != null && plot.longitude != null">
                                    {{ Number(plot.latitude).toFixed(5) }}, {{ Number(plot.longitude).toFixed(5) }}
                                  </template>
                                  <template v-else>—</template>
                                </td>
                                <td>
                                  <template v-if="plot.radius != null && plot.radius !== ''">
                                    {{ plot.radius }}{{ previewRadiusUnit ? ` ${previewRadiusUnit}` : '' }}
                                  </template>
                                  <template v-else>—</template>
                                </td>
                                <td>{{ plot.assignedTo || '—' }}</td>
                                <td>
                                  <v-chip
                                    x-small
                                    :color="plot.importStatus === 'ok' ? 'primary' : 'error'"
                                    outlined
                                    class="justify-center"
                                  >
                                    {{ plot.importStatus === 'ok' ? $t('success') : $t('failed') }}
                                  </v-chip>
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-expansion-panel-content>
                      </v-expansion-panel>
                    </v-expansion-panels>
                  </v-card>

                  <v-divider class="my-5 px-0" />

                  <v-card-actions class="px-0">
                    <v-btn text color="primary" class="px-2" @click="backToUpload">
                      {{ $t('carbonCredit.bulkUpload.back') }}
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      class="mr-2"
                      outlined
                      color="primary"
                      width="112"
                      @click="closeDialog"
                    >
                      {{ $t('cancel') }}
                    </v-btn>
                    <v-btn
                      width="112"
                      color="primary"
                      class="btn-11 btn-22"
                      :disabled="!canImport"
                      @click="runImport"
                    >
                      {{ $t('oma.import') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import CarbonCreditService from "@/_services/CarbonCreditService";

export default {
  name: "BulkUploadDialog",
  props: {
    defaultOrganizationId: {
      type: [Number, String],
      default: null,
    },
    isSuperAdmin: {
      type: Boolean,
      default: false,
    },
    afterSuccess: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      dialogOpen: false,
      tab: 0,
      dragging: false,
      file: null,
      fileName: "",
      fileError: null,
      previewLoading: false,
      importLoading: false,
      previewData: null,
      selectedOrganizationId: null,
      hasSubmittedImport: false,
      importFailed: false,
    };
  },
  computed: {
    ...mapGetters("organization", ["allOrganizations", "loadingOrganizations"]),
    showPreviewStats() {
      return !!this.previewData && !this.hasSubmittedImport && !this.importFailed;
    },
    importErrorList() {
      return this.previewErrorList;
    },
    organizationList() {
      let list = (this.allOrganizations || [])
        .filter((org) => !org.isSubOrganization && !org.parentId)
        .map((org) => ({
          id: org.id,
          name: org.name,
          code: org.code,
          country: org.country,
        }));
      if (
        this.defaultOrganizationId &&
        !list.some((o) => o.id === this.defaultOrganizationId)
      ) {
        const userOrg = this.$store.getters.getUser?.user_organization;
        if (userOrg?.id) {
          list = [
            {
              id: userOrg.id,
              name: userOrg.name || `Organization ${userOrg.id}`,
            },
            ...list,
          ];
        }
      }
      return list;
    },
    isBusy() {
      return this.previewLoading || this.importLoading;
    },
    busyMessage() {
      if (this.importLoading) {
        return this.$t("carbonCredit.bulkUpload.importingNote");
      }
      return this.$t("carbonCredit.bulkUpload.previewingNote");
    },
    effectiveOrganizationId() {
      return this.selectedOrganizationId || this.defaultOrganizationId || null;
    },
    selectedOrganizationName() {
      if (!this.effectiveOrganizationId) return "";
      const org = this.organizationList.find(
        (o) => o.id === this.effectiveOrganizationId
      );
      return org?.name || String(this.effectiveOrganizationId);
    },
    templateDownloadUrl() {
      return `${process.env.BASE_URL || "/"}files/carbon-credit/carbon-credit-bulk-upload-template.xlsx`;
    },
    canPreview() {
      return (
        !!this.file &&
        !!this.effectiveOrganizationId &&
        !this.fileError &&
        !this.isBusy
      );
    },
    previewHasRowErrors() {
      if (this.previewProjects.some((r) => r.importStatus === "error")) {
        return true;
      }
      if (this.previewFarmLinks.some((r) => r.importStatus === "error")) {
        return true;
      }
      return this.previewPlotRequests.some((req) => {
        if (req.importStatus === "error") return true;
        return (req.plots || []).some((p) => p.importStatus === "error");
      });
    },
    previewHasBlockingIssues() {
      return (
        this.previewErrorList.length > 0 || this.previewHasRowErrors
      );
    },
    canImport() {
      return (
        !!this.previewData &&
        !!this.file &&
        !this.isBusy &&
        !this.importFailed &&
        !this.previewHasBlockingIssues
      );
    },
    previewSuccess() {
      return !!this.previewData && !this.previewHasBlockingIssues;
    },
    isDryRunPreview() {
      return this.previewData?.dryRun === true && !this.hasSubmittedImport;
    },
    previewProgressPercent() {
      if (!this.previewData) return 0;
      const { total, success } = this.previewStats;
      if (!total) return this.previewSuccess ? 100 : 0;
      return Math.min(100, Math.max(0, Math.round((success / total) * 100)));
    },
    previewStats() {
      const statuses = [];
      this.previewProjects.forEach((r) => statuses.push(r.importStatus));
      this.previewFarmLinks.forEach((r) => statuses.push(r.importStatus));
      this.previewPlotRequests.forEach((req) => {
        if (req.plots?.length) {
          req.plots.forEach((p) => statuses.push(p.importStatus));
        } else {
          statuses.push(req.importStatus);
        }
      });
      const total = statuses.length;
      const failed = statuses.filter((s) => s === "error").length;
      const success = total - failed;
      return {
        total,
        failed,
        success: Math.max(0, success),
      };
    },
    previewSummary() {
      return this.previewData?.summary || null;
    },
    previewRadiusUnit() {
      const unit = this.previewData?.config?.radiusUnit;
      return unit ? String(unit).toLowerCase() : "m";
    },
    previewErrorList() {
      const out = [];
      const s = this.previewSummary;
      if (!s) return out;
      const pushErrors = (sheetKey, sheetLabel) => {
        (s[sheetKey]?.errors || []).forEach((e) => {
          const rowNum = e.dataRow != null ? e.dataRow : e.row;
          out.push({
            id: `${sheetKey}-err-${rowNum}-${out.length}`,
            location: `${sheetLabel}, row ${rowNum}`,
            message: e.message,
          });
        });
      };
      pushErrors("projects", "Projects");
      pushErrors("farms", "Farms");
      pushErrors("plots", "Plots");
      return out;
    },
    previewWarningList() {
      const out = [];
      const s = this.previewSummary;
      if (!s) return out;
      const pushWarnings = (sheetKey, sheetLabel) => {
        (s[sheetKey]?.warnings || []).forEach((w) => {
          if (this.isIgnorableBulkWarning(w.message)) return;
          const rowNum = w.dataRow != null ? w.dataRow : w.row;
          out.push({
            id: `${sheetKey}-warn-${rowNum}-${out.length}`,
            location: `${sheetLabel}, row ${rowNum}`,
            message: w.message,
          });
        });
      };
      pushWarnings("projects", "Projects");
      pushWarnings("farms", "Farms");
      pushWarnings("plots", "Plots");
      return out;
    },
    previewProjects() {
      const rows = this.previewData?.preview?.projects || [];
      return rows.map((p, i) => ({
        rowKey: `proj-${i}-${p.row}`,
        row: p.row,
        projectTitle: p.projectTitle,
        projectType: p.projectType,
        country: p.country || "—",
        action: p.action,
        existingProjectId: p.existingProjectId,
        importStatus: p.importStatus,
        message: p.message || "",
      }));
    },
    previewFarmLinks() {
      const rows = this.previewData?.preview?.farmLinks || [];
      return rows.map((f, i) => ({
        rowKey: `farm-${i}-${f.row}`,
        row: f.row,
        projectTitle: f.projectTitle,
        projectType: f.projectType,
        farmName: f.farmName,
        farmerEmail: f.farmerEmail,
        action: f.action,
        importStatus: f.importStatus,
        message: f.message || "",
      }));
    },
    previewPlotRequests() {
      const rows = this.previewData?.preview?.plotRequests || [];
      return rows.map((r, i) => ({
        rowKey: `req-${i}-${r.farmId || r.row || i}`,
        ...r,
      }));
    },
    projectHeaders() {
      return [
        { text: this.$t("carbonCredit.bulkUpload.colRow"), value: "row", width: "60px", class: "black--text" },
        { text: this.$t("carbonCredit.projectTitle"), value: "projectTitle", class: "black--text" },
        { text: this.$t("carbonCredit.projectType"), value: "projectType", class: "black--text" },
        { text: this.$t("carbonCredit.country"), value: "country", class: "black--text" },
        { text: this.$t("carbonCredit.bulkUpload.colAction"), value: "action", class: "black--text" },
        { text: this.$t("oma.status"), value: "importStatus", align: "center", class: "black--text", sortable: false },
      ];
    },
    farmHeaders() {
      return [
        { text: this.$t("carbonCredit.bulkUpload.colRow"), value: "row", width: "60px", class: "black--text" },
        { text: this.$t("carbonCredit.projectTitle"), value: "projectTitle", class: "black--text" },
        { text: this.$t("carbonCredit.bulkUpload.colFarmName"), value: "farmName", class: "black--text" },
        { text: this.$t("carbonCredit.bulkUpload.colFarmerEmail"), value: "farmerEmail", class: "black--text" },
        { text: this.$t("carbonCredit.bulkUpload.colAction"), value: "action", class: "black--text" },
        { text: this.$t("carbonCredit.bulkUpload.colDetails"), value: "message", class: "black--text" },
        { text: this.$t("oma.status"), value: "importStatus", align: "center", class: "black--text", sortable: false },
      ];
    },
    projectHeadersWithMessage() {
      const headers = [...this.projectHeaders];
      const statusIdx = headers.findIndex((h) => h.value === "importStatus");
      headers.splice(statusIdx, 0, {
        text: this.$t("carbonCredit.bulkUpload.colDetails"),
        value: "message",
        class: "black--text",
      });
      return headers;
    },
  },
  methods: {
    ...mapActions("organization", ["fetchOrganizations"]),
    async open() {
      this.resetState();
      this.dialogOpen = true;
      if (!this.allOrganizations?.length) {
        await this.fetchOrganizations();
      }
      this.selectedOrganizationId = this.defaultOrganizationId || null;
    },
    resetState() {
      this.tab = 0;
      this.file = null;
      this.fileName = "";
      this.fileError = null;
      this.previewData = null;
      this.previewLoading = false;
      this.importLoading = false;
      this.selectedOrganizationId = null;
      this.hasSubmittedImport = false;
      this.importFailed = false;
      this.dragging = false;
    },
    filterOrganizations(item, queryText) {
      if (!queryText) return true;
      const q = queryText.toLowerCase();
      return (item.name || "").toLowerCase().includes(q);
    },
    onOrganizationChange() {
      this.previewData = null;
      this.hasSubmittedImport = false;
      this.importFailed = false;
      this.tab = 0;
    },
    closeDialog(force = false) {
      if (!force && this.isBusy) return;
      this.dialogOpen = false;
      this.resetState();
    },
    backToUpload() {
      this.tab = 0;
      this.previewData = null;
      this.hasSubmittedImport = false;
      this.importFailed = false;
    },
    clearFile() {
      this.file = null;
      this.fileName = "";
      this.fileError = null;
      this.previewData = null;
      this.hasSubmittedImport = false;
      this.importFailed = false;
      this.tab = 0;
    },
    unwrapBulkUploadPayload(res) {
      if (!res) return null;
      const inner = res.data;
      if (
        inner &&
        typeof inner.success === "boolean" &&
        (inner.summary || inner.preview !== undefined || inner.dryRun !== undefined)
      ) {
        return inner;
      }
      if (
        typeof res.success === "boolean" &&
        (res.summary || res.preview !== undefined || res.dryRun !== undefined)
      ) {
        return res;
      }
      return inner || res;
    },
    isImportSuccessful(res) {
      const payload = this.unwrapBulkUploadPayload(res);
      return payload?.success === true;
    },
    isIgnorableBulkWarning(message) {
      if (!message) return false;
      return /not approved|not linked to a carbon credit project.*bulk import/i.test(
        message
      );
    },
    rowImportStatusLabel(item, { farmApplication = false } = {}) {
      if (item.importStatus === "error") {
        return this.$t("failed");
      }
      if (farmApplication) {
        return this.$t("carbonCredit.bulkUpload.statusPendingReview");
      }
      return this.$t("success");
    },
    rowImportStatusColor(item, { farmApplication = false } = {}) {
      if (item.importStatus === "error") return "error";
      if (farmApplication) return "warning";
      return "primary";
    },
    validateFile(file) {
      if (!file) return false;
      const name = (file.name || "").toLowerCase();
      if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
        this.fileError = this.$t("carbonCredit.bulkUpload.invalidFileFormat");
        return false;
      }
      this.fileError = null;
      return true;
    },
    onFileChange(event) {
      this.dragging = false;
      const file = event.target?.files?.[0];
      if (!file) return;
      if (!this.validateFile(file)) {
        this.file = null;
        this.fileName = "";
        return;
      }
      this.file = file;
      this.fileName = file.name;
      this.previewData = null;
      this.importFailed = false;
      this.tab = 0;
      if (event.target) event.target.value = "";
    },
    onDrop(event) {
      this.dragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (!file) return;
      if (!this.validateFile(file)) {
        this.file = null;
        this.fileName = "";
        return;
      }
      this.file = file;
      this.fileName = file.name;
      this.previewData = null;
      this.importFailed = false;
      this.tab = 0;
    },
    actionLabel(action) {
      if (action === "create") return this.$t("carbonCredit.bulkUpload.actionCreate");
      if (action === "reuse") return this.$t("carbonCredit.bulkUpload.actionReuse");
      if (action === "error") return this.$t("failed");
      return action || "—";
    },
    farmActionLabel(action) {
      if (action === "link") return this.$t("carbonCredit.bulkUpload.actionLink");
      if (action === "already_linked") return this.$t("carbonCredit.bulkUpload.actionAlreadyLinked");
      if (action === "error") return this.$t("failed");
      return action || "—";
    },
    actionChipColor(action) {
      if (action === "error") return "error";
      if (action === "reuse" || action === "already_linked") return "grey";
      return "primary";
    },
    async runPreview() {
      if (!this.canPreview) return;
      if (!this.effectiveOrganizationId) {
        this.fileError = this.$t("carbonCredit.bulkUpload.missingOrganization");
        return;
      }
      this.previewLoading = true;
      this.fileError = null;
      this.hasSubmittedImport = false;
      this.importFailed = false;
      try {
        const res = await CarbonCreditService.bulkUploadCarbonCredit({
          file: this.file,
          organizationId: this.effectiveOrganizationId,
          dryRun: true,
        });
        this.previewData = this.unwrapBulkUploadPayload(res);
        this.tab = 1;
      } catch (err) {
        const msg =
          err?.data?.message ||
          err?.response?.data?.message ||
          err?.message ||
          this.$t("carbonCredit.bulkUpload.serverError");
        this.fileError = msg;
        this.$notify({ text: msg, type: "error" });
      } finally {
        this.previewLoading = false;
      }
    },
    async runImport() {
      if (!this.canImport) return;
      this.importLoading = true;
      this.importFailed = false;
      try {
        const res = await CarbonCreditService.bulkUploadCarbonCredit({
          file: this.file,
          organizationId: this.effectiveOrganizationId,
          dryRun: false,
          defaultAgreementSigner: "Bulk Import",
        });
        const payload = this.unwrapBulkUploadPayload(res);
        if (this.isImportSuccessful(res)) {
          this.importLoading = false;
          this.$notify({
            text: this.$t("carbonCredit.bulkUpload.importSuccess"),
            type: "success",
          });
          if (typeof this.afterSuccess === "function") {
            await this.afterSuccess();
          }
          this.closeDialog(true);
          return;
        }
        this.previewData = payload;
        this.importFailed = true;
        this.tab = 0;
        this.$notify({
          text: this.$t("carbonCredit.bulkUpload.importFailed"),
          type: "error",
        });
      } catch (err) {
        const msg =
          err?.data?.message ||
          err?.response?.data?.message ||
          err?.message ||
          this.$t("carbonCredit.bulkUpload.serverError");
        this.$notify({ text: msg, type: "error" });
      } finally {
        this.importLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dropzone {
  height: 280px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed $gray;
  border-radius: 8px;
}

.dropzone label {
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.dropzone label:hover,
.dropzone label.active {
  background-color: #f5f5f5;
}

.bulk-upload-table {
  ::v-deep thead tr th {
    background-color: #00bd73 !important;
    color: #fff !important;
    font-weight: 600;
  }
}

.bulk-upload-message-list {
  font-size: 13px;
  line-height: 1.45;
  max-height: 160px;
  overflow-y: auto;
}

.bulk-upload-org-field {
  max-width: 320px;
}
</style>
